import mergeWith from 'lodash/mergeWith';
import uniqBy from 'lodash/uniqBy';
import orderBy from 'lodash/orderBy';
import keyBy from 'lodash/keyBy';
import { ScenariosConstants } from '../../xlsxExport.constants';
import { ScenarioReportDetail } from '@common/schemas/scenarioDetails';
import { ReportField, ReportName } from '@common/constants/scenariosConstants';

const {
  BASE_PREFIX, COMPARE_PREFIX, VARIANCE_PREFIX, NoDetailValue,
} = ScenariosConstants;

const GROUPING_FIELDS = ['COMPANY_CODE', 'COUNTRY_TAX_TYPE'];
const COMPOSITE_KEY_FIELD = GROUPING_FIELDS.join('-');

const SUM_NAME_WIDTH = 300;
const DETAIL_NAME_WIDTH = 300;
const TOTAL_COLUMN_WIDTH = 150;
const VALUE_WIDTH = 100;

type MergedReportDetail = Omit<ScenarioReportDetail, 'value'> & {
  value: number | string,
  varianceValue: number | string,
  compareValue?: number | string,
}

export type ReportRow = {
  COMPANY_CODE: string,
  COUNTRY_TAX_TYPE: string,
  [key: string]: string | number | null,
}

export type TotalColumn = Record<string, {base: number | null, compare?: number | null, variance?: number | null}>;

export const getCompositeKey = (report: ReportRow) =>
  GROUPING_FIELDS.map(field => `${report[field]}`).join('-');

type Value = number | string | null;
export const getRowPartials = (isComparing?: boolean) => isComparing
  ? {
    size: 3,
    headers: (header: string) => [header, null, null],
    subHeaders: () => ['1st scenario', '2nd scenario', 'Variance'],
    sum: (data: { base: Value, compare?: Value, variance?: Value }) =>
      [data.base, data.compare, data.variance],
    detail: (detail?: {
      value: Value,
      compareValue?: Value,
      varianceValue?: Value,
    }) => [detail?.value, detail?.compareValue, detail?.varianceValue],
    width: () => [VALUE_WIDTH, VALUE_WIDTH, VALUE_WIDTH],
    totalHeaders: () => ['Total', null, null],
    totalHeadersSpan: () => [null, null, null],
    totalSubHeaders: () => ['1st scenario', '2nd scenario', 'Variance'],
    total: (total: { base: Value, compare?: Value, variance?: Value }) =>
      [total.base, total.compare, total.variance],
    totalColumnsWidth: () => [TOTAL_COLUMN_WIDTH, TOTAL_COLUMN_WIDTH, TOTAL_COLUMN_WIDTH],
  }
  : {
    size: 1,
    headers: (header: Value) => [header],
    sum: (data: { base: Value }) => [data.base],
    detail: (detail?: { value: Value }) => [detail?.value || null],
    width: () => [VALUE_WIDTH],
    totalHeaders: () => ['Total'],
    totalHeadersSpan: () => [null],
    total: (total: { base: Value }) => [total.base],
    totalColumnsWidth: () => [TOTAL_COLUMN_WIDTH],
  };

type RowPartials = ReturnType<typeof getRowPartials>;
export const getUniqDetails = (
  groupedData: Record<string, { details: MergedReportDetail[] }>,
  groupingColumns?: {key: string}[]
) => {
  const allDetails = groupingColumns?.flatMap(column => groupedData[column.key].details) || [];

  return uniqBy(allDetails, 'detailId');
};
const isMatchingDetail = (
  detail: ScenarioReportDetail,
  reportRow: ReportRow
) =>
  detail.companyCode === reportRow.COMPANY_CODE && detail.countryTaxType === reportRow.COUNTRY_TAX_TYPE;

type MergeScenarioDetailsParams = {
    compareDetails: ScenarioReportDetail[],
    baseDetails: ScenarioReportDetail[],
    reportRow: ReportRow,
}
const mergeScenarioDetails = ({ compareDetails, baseDetails, reportRow }: MergeScenarioDetailsParams) => {
  const compareDetailsMap = compareDetails
    .filter(compareDetail => isMatchingDetail(compareDetail, reportRow))
    .reduce<Record<string, ScenarioReportDetail>>((accu, detail) => {
      accu[detail.detailId] = detail;
      return accu;
    }, {});
  const baseDetailsMap: Record<string, ScenarioReportDetail> = {};
  const mergedDetails = baseDetails
    .filter(detail => isMatchingDetail(detail, reportRow))
    .map(detail => {
      baseDetailsMap[detail.detailId] = detail;

      if (compareDetailsMap[detail.detailId]) {
        return {
          ...detail,
          compareValue: compareDetailsMap[detail.detailId].value,
          detailExists: true,
          compareDetailExists: true,
          varianceValue: compareDetailsMap[detail.detailId].value - detail.value,
        } as MergedReportDetail;
      }
      return {
        ...detail,
        detailExists: true,
        compareDetailExists: false,
        varianceValue: NoDetailValue,
      } as MergedReportDetail;
    });

  compareDetails.forEach(detail => {
    if (!baseDetailsMap[detail.detailId]) {
      mergedDetails.push({
        ...detail,
        value: NoDetailValue,
        compareValue: detail.value,
        detailExists: false,
        compareDetailExists: true,
        varianceValue: NoDetailValue,
      } as MergedReportDetail);
    }
  });
  return mergedDetails;
};

type BundleSumAndDetailsParams = {
  isComparing?: boolean,
  displayedReportFields: ReportField[],
  reportsData: Record<string, ReportRow[]>,
  detailsData: {
    baseDetailsData: {
      [reportName: string]: {
        [fromField: string]: {
          [countryTaxType: string]: ScenarioReportDetail[]
        }
      }
    },
    compareDetailsData: {
      [reportName: string]: {
        [fromField: string]: {
          [countryTaxType: string]: ScenarioReportDetail[]
        }
      }
    },
  }
}
export const bundleSumAndDetails = ({
  isComparing,
  displayedReportFields,
  reportsData,
  detailsData,
}: BundleSumAndDetailsParams) => displayedReportFields.map(reportField => ({
  reportField,
  groupedData: keyBy((Object.keys(reportsData) as ReportName[]).flatMap(reportName => {
    const reports = reportsData[reportName];
    const reportDataByGroupingField = keyBy(reports, getCompositeKey);

    return reportsData[reportName].map(reportRow => {
      const groupValue = getCompositeKey(reportRow);
      const from = typeof reportField.from === 'object' ? reportField.from[reportName] : reportField.from;
      const fieldEditable = typeof reportField.editable === 'boolean'
        ? reportField.editable
        : reportField.editable?.[reportName];

      if (!from) {
        return {
          from,
          reportField,
          [COMPOSITE_KEY_FIELD]: groupValue,
          base: null,
          compare: null,
          details: [],
        };
      }

      const { baseDetailsData, compareDetailsData } = detailsData;

      const baseDetails = baseDetailsData[reportName]?.[from]?.[reportRow.COUNTRY_TAX_TYPE] || [];
      const compareDetails = compareDetailsData?.[reportName]?.[from]?.[reportRow.COUNTRY_TAX_TYPE] || [];

      return {
        from,
        reportField,
        [COMPOSITE_KEY_FIELD]: groupValue,
        base: reportDataByGroupingField?.[groupValue][`${isComparing ? BASE_PREFIX : ''}${from}`],
        compare: reportDataByGroupingField?.[groupValue][`${COMPARE_PREFIX}${from}`],
        variance: reportDataByGroupingField?.[groupValue][`${VARIANCE_PREFIX}${from}`],
        details: fieldEditable ? mergeScenarioDetails({ baseDetails, compareDetails, reportRow }) : [],
      };
    });
  }), COMPOSITE_KEY_FIELD),
}));

export const getGroupingColumns = (reportsData: Record<string, ReportRow[]>) => {
  const reports = Object.keys(reportsData).flatMap(reportName => reportsData[reportName]);

  const groupedColumns = reports.map(report => ({
    key: getCompositeKey(report),
    ...GROUPING_FIELDS.reduce((acc, groupingField) => ({
      ...acc,
      [groupingField]: report[groupingField],
    }), {}),
  }));

  return orderBy(groupedColumns, GROUPING_FIELDS);
};

type BuildSumRowParams = {
    groupingColumns: {key: string}[],
    reportField: ReportField,
    groupedData: Record<string, Parameters<ReturnType<typeof getRowPartials>['sum']>['0']>,
    rowPartials: RowPartials,
    totalColumn: TotalColumn,
}

const buildSumRow = ({
  groupingColumns,
  reportField,
  groupedData,
  rowPartials,
  totalColumn,
}: BuildSumRowParams) => ({
  rows: [[
    reportField.label,
    null,
    ...rowPartials.total(totalColumn[reportField.label]),
    ...groupingColumns.flatMap(column => rowPartials.sum(groupedData[column.key])),
  ]],
  rowsMeta: {
    ...(reportField.exportFormat ? { exportFormat: reportField.exportFormat } : {}),
    ...(reportField.type ? { type: reportField.type } : {}),
  },
});

type BuildDetailRowsParams = {
    groupingColumns: {key: string}[],
    groupedData: Record<string, { details: MergedReportDetail[] }>,
    reportField: ReportField,
    rowPartials: RowPartials,
    padRight?: number,
}
export const buildDetailRows = ({
  groupingColumns,
  groupedData,
  reportField,
  rowPartials,
  padRight = 0,
}: BuildDetailRowsParams) => {
  const uniqDetails = getUniqDetails(groupedData, groupingColumns);

  const groupedColumnToDetailsMap = groupingColumns.reduce<Record<string, Record<string, MergedReportDetail>>>(
    (acc, column) => ({
      ...acc,
      [column.key]: keyBy(groupedData[column.key].details, 'detailId'),
    }), {});

  const detailRows = uniqDetails.map(detail => {
    const groupedColumns = groupingColumns.flatMap(column => {
      const details = groupedColumnToDetailsMap[column.key];
      if (detail.detailId in details) {
        return rowPartials.detail(details[detail.detailId]);
      }
      return rowPartials.detail();
    });

    return [
      ...new Array(padRight).fill(null),
      detail.account,
      detail.detailName,
      ...rowPartials.totalHeadersSpan(),
      ...groupedColumns,
    ];
  });

  return {
    rows: detailRows,
    rowsMeta: detailRows.map(() => ({
      ...(reportField.exportFormat ? { exportFormat: reportField.exportFormat } : {}),
    })),
  };
};
type BuildRowsParams = {
    groupingColumns: {key: string}[],
    data: ReturnType<typeof bundleSumAndDetails>,
    rowPartials: RowPartials,
    totalColumn: TotalColumn,
}
export const buildRows = ({ groupingColumns, data, rowPartials, totalColumn }: BuildRowsParams) =>
  mergeWith(
    { rows: [], rowsMeta: [] },
    ...data.flatMap(({ reportField, groupedData }) => [
      buildSumRow({ groupingColumns, reportField, groupedData, rowPartials, totalColumn }),
      buildDetailRows({ groupingColumns, reportField, groupedData, rowPartials }),
    ]),
    (objValue: (string| number | null)[][], srcValue: (string| number | null)[][]) => objValue.concat(srcValue)
  ) as (string| number | null)[][];


type BuildHeadersParams = {
    groupingColumns: {key: string}[],
    rowPartials: RowPartials,
    padRight?: number,
}
export const buildHeaders = ({ groupingColumns, rowPartials, padRight = 2 }: BuildHeadersParams) => {
  const groupedRows = GROUPING_FIELDS.map((field, index) => {
    const columns = groupingColumns.flatMap(column => rowPartials.headers(column[field as keyof typeof column]));

    // append Total Column Label only on the last header row
    const totalHeaders = index === GROUPING_FIELDS.length - 1
      ? rowPartials.totalHeaders()
      : rowPartials.totalHeadersSpan();

    return [
      ...new Array(padRight).fill(null),
      ...totalHeaders,
      ...columns,
    ];
  });

  return {
    headerRows: [
      ...groupedRows,
      ...(rowPartials.subHeaders ? [[
        ...new Array(padRight).fill(null),
        ...rowPartials.totalSubHeaders(),
        ...groupingColumns.flatMap(() => rowPartials.subHeaders()),
      ]] : []),
    ],
    headerMeta: [
      ...new Array(GROUPING_FIELDS.length).fill({}),
      ...(rowPartials.subHeaders ? [{}] : []),
    ],
  };
};

type BuildColumnsMetaParams = {
    groupingColumns: {key: string}[],
    rowPartials: RowPartials,
}
export const buildColumnsMeta = ({ groupingColumns, rowPartials }: BuildColumnsMetaParams) => ({
  columnsMeta: [
    { width: SUM_NAME_WIDTH },
    { width: DETAIL_NAME_WIDTH },
    ...rowPartials.totalColumnsWidth().map(width => ({ width })),
    ...groupingColumns.flatMap(() => rowPartials.width()).map(width => ({ width })),
  ],
});
